import { eq } from "drizzle-orm";
import { db_schema } from "./schema";
import {
  createEmptySnapshot,
  normalizeSnapshot,
  type DbSchemaSnapshot,
} from "./db-schema-snapshot";
import { syncProjectTableSchemas } from "./table-schema-store";
import type { PrimaryDB } from "./index";

export async function getProjectSchemaSnapshot(
  db: PrimaryDB,
  projectId: number,
): Promise<DbSchemaSnapshot> {
  const [row] = await db
    .select({ snapshot: db_schema.snapshot })
    .from(db_schema)
    .where(eq(db_schema.projectId, projectId))
    .limit(1);

  if (!row?.snapshot) {
    return createEmptySnapshot();
  }

  return normalizeSnapshot(row.snapshot as DbSchemaSnapshot);
}

export async function setProjectSchemaSnapshot(
  db: PrimaryDB,
  projectId: number,
  snapshot: DbSchemaSnapshot,
): Promise<DbSchemaSnapshot> {
  const normalized = normalizeSnapshot(snapshot);

  const [existing] = await db
    .select({ id: db_schema.id })
    .from(db_schema)
    .where(eq(db_schema.projectId, projectId))
    .limit(1);

  if (existing?.id) {
    await db
      .update(db_schema)
      .set({
        snapshot: normalized,
        updatedAt: new Date(),
      })
      .where(eq(db_schema.id, existing.id));
  } else {
    await db.insert(db_schema).values({
      projectId,
      snapshot: normalized,
    });
  }

  await syncProjectTableSchemas(db, projectId, normalized);

  return normalized;
}

export async function mutateProjectSchemaSnapshot(
  db: PrimaryDB,
  projectId: number,
  mutator: (snapshot: DbSchemaSnapshot) => DbSchemaSnapshot,
): Promise<DbSchemaSnapshot> {
  const current = await getProjectSchemaSnapshot(db, projectId);
  const next = normalizeSnapshot(mutator(current));
  return setProjectSchemaSnapshot(db, projectId, next);
}
